import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, ChevronRight, LogOut, Globe, Check, User, Trash2, PauseCircle, AtSign, Bell, ShoppingCart, Gift, Star, Coins, TrendingUp, Download, Shield, Map, Eye, FileText, HelpCircle, Info, Sparkles, Crown, Award, Moon, Sun, Lock, Building2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { AdeLoadingSpinner } from "@/components/AdeLoadingSpinner";
import { PushNotificationSettings } from "@/components/PushNotifications";
import { CoinShop } from "@/components/CoinShop";
import { useTheme } from "@/contexts/ThemeContext";
import { BillingDataDialog } from "@/components/BillingDataDialog";
import { useLang, setLang as setAppLang } from "@/i18n";

const LANGUAGES = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
];

export default function Settings() {
  const navigate = useNavigate();
  const { isDark, toggle: toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  
  // User data
  const [user, setUser] = useState(null);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [usernameCheck, setUsernameCheck] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  useEffect(() => {
    if (!newUsername || newUsername === username) { setUsernameCheck(null); setCheckingUsername(false); return; }
    setCheckingUsername(true);
    const t = setTimeout(() => {
      api.get(`/account/username/check?u=${encodeURIComponent(newUsername)}`)
        .then((r) => setUsernameCheck(r.data))
        .catch(() => setUsernameCheck(null))
        .finally(() => setCheckingUsername(false));
    }, 350);
    return () => clearTimeout(t);
  }, [newUsername, username]);
  const [displayUsername, setDisplayUsername] = useState(false);
  const [bio, setBio] = useState("");
  const [age, setAge] = useState("");
  const [city, setCity] = useState("");
  
  // Privacy settings
  const [isPrivate, setIsPrivate] = useState(false);
  const [mapVisible, setMapVisible] = useState(true);
  const [showGiftsReceived, setShowGiftsReceived] = useState(true);
  const [discoverable, setDiscoverable] = useState(true);
  const [readReceipts, setReadReceipts] = useState(true);
  const [hideIdentity, setHideIdentity] = useState(false);
  
  // Language setting
  const { t } = useLang();
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return localStorage.getItem('ade_language') || 'it';
  });
  
  // Wallet data
  const [wallet, setWallet] = useState({ balance: 0, gifts_sent: 0, gifts_received: 0 });
  const [showBilling, setShowBilling] = useState(false);
  
  // Coin Shop
  const [showCoinShop, setShowCoinShop] = useState(false);
  
  // Dialogs
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [islandName, setIslandName] = useState("");
  const [newIslandName, setNewIslandName] = useState("");
  const [islandNameAvailable, setIslandNameAvailable] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [meResponse, walletResponse] = await Promise.all([
          api.get('/auth/me'),
          api.get('/wallet').catch(() => ({ data: { balance: 0, gifts_sent: 0, gifts_received: 0 } }))
        ]);
        
        setUser(meResponse.data);
        setName(meResponse.data.name || "");
        setUsername(meResponse.data.username || "");
        setNewUsername(meResponse.data.username || "");
        setDisplayUsername(meResponse.data.display_username || false);
        setBio(meResponse.data.bio || "");
        setAge(meResponse.data.age?.toString() || "");
        setCity(meResponse.data.city || "");
        setIsPrivate(meResponse.data.is_private || false);
        setShowGiftsReceived(meResponse.data.settings?.show_gifts_received !== false);
        setDiscoverable(meResponse.data.settings?.discoverable !== false);
        setReadReceipts(meResponse.data.settings?.read_receipts !== false);
        setHideIdentity(meResponse.data.settings?.hide_identity === true);
        setIslandName(meResponse.data.island_name || "");
        setWallet(walletResponse.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/profile', {
        name: name.trim(),
        bio: bio.trim() || null,
        age: age ? parseInt(age) : null,
        city: city.trim() || null,
        is_private: isPrivate
      });
      // Notify listeners (e.g. ProfessionalSuggestions on Home) that the profile
      // — and possibly the city — has changed, so they can re-fetch.
      window.dispatchEvent(new Event('ade-user-updated'));
      setActiveSection(null);
    } catch (error) {
      toast.error(t('saveError'));
    } finally {
      setSaving(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {}
    localStorage.removeItem('token');
    navigate('/login', { replace: true });
  };
  
  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    localStorage.setItem('ade_language', langCode);
    setAppLang(langCode);
    toast.success(`Lingua cambiata in ${LANGUAGES.find(l => l.code === langCode)?.name}`);
  };
  
  const handleUpdateUsername = async () => {
    if (!newUsername || newUsername.length < 3) {
      toast.error("Username deve avere almeno 3 caratteri");
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/account/username', { new_username: newUsername });
      setUsername(newUsername.toLowerCase());
      toast.success("Username aggiornato!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleDisplayName = async () => {
    try {
      const newValue = !displayUsername;
      await api.put('/account/display-name', { display_username: newValue });
      setDisplayUsername(newValue);
      toast.success(newValue ? "Verrà mostrato il tuo username" : "Verrà mostrato il tuo nome");
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };
  
  const handleSuspendAccount = async () => {
    try {
      await api.post('/account/suspend');
      toast.success("Account sospeso. Puoi riattivarlo effettuando il login.");
      handleLogout();
    } catch (error) {
      toast.error("Errore nella sospensione");
    }
    setShowSuspendDialog(false);
  };
  
  const handleDeleteAccount = async () => {
    try {
      await api.delete('/account/delete');
      toast.success("Account eliminato permanentemente");
      localStorage.removeItem('token');
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
    setShowDeleteDialog(false);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-gray-300">...</div>
      </div>
    );
  }
  
  // Section views
  const renderAccount = () => (
    <div className="px-4 py-4 space-y-4">
      <div>
        <label className="text-xs text-gray-400 uppercase tracking-wide">Nome</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 h-10 border-gray-200 rounded-lg"
        />
      </div>
      <div>
        <label className="text-xs text-gray-400 uppercase tracking-wide">Bio</label>
        <Textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          className="mt-1 border-gray-200 rounded-lg resize-none"
          rows={3}
          maxLength={150}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wide">Età</label>
          <Input
            type="number"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            className="mt-1 h-10 border-gray-200 rounded-lg"
            min="18"
            max="99"
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wide">Città</label>
          <Input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="mt-1 h-10 border-gray-200 rounded-lg"
          />
        </div>
      </div>
      <Button
        onClick={handleSave}
        disabled={saving}
        variant="outline"
        className="w-full h-10 rounded-lg border-gray-200 text-gray-700 font-normal mt-4"
      >
        {saving ? '...' : 'Salva'}
      </Button>
    </div>
  );
  
  const renderPrivacy = () => (
    <div className="px-4 py-4 space-y-4">
      <div className="flex items-center justify-between py-2">
        <div>
          <p className="text-sm text-gray-900">{t('privateProfile')}</p>
          <p className="text-xs text-gray-400">Solo i collegamenti vedono i dettagli</p>
        </div>
        <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
      </div>
      <div className="flex items-center justify-between py-2">
        <div>
          <p className="text-sm text-gray-900">Visibile sulla mappa</p>
          <p className="text-xs text-gray-400">Gli altri possono vederti nelle vicinanze</p>
        </div>
        <Switch checked={mapVisible} onCheckedChange={setMapVisible} />
      </div>
      <div className="flex items-center justify-between py-2">
        <div>
          <p className="text-sm text-gray-900">Mostra regali ricevuti</p>
          <p className="text-xs text-gray-400">Gli altri vedono i Gift ricevuti sulle tue foto</p>
        </div>
        <Switch checked={showGiftsReceived} onCheckedChange={async (val) => {
          setShowGiftsReceived(val);
          try {
            await api.put('/profile/settings', { show_gifts_received: val });
          } catch { toast.error(t('saveError')); }
        }} />
      </div>

      <Separator className="my-3" />
      <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold">ADE ISLAND</p>

      {/* Island Name editor */}
      <div>
        <p className="text-sm text-gray-900 mb-1">Nome della tua Isola</p>
        <p className="text-xs text-gray-400 mb-2">Questo e l'unico modo per trovarti quando il GPS e spento</p>
        <div className="flex gap-2">
          <Input
            value={newIslandName || islandName}
            onChange={async (e) => {
              const val = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
              setNewIslandName(val);
              if (val.length >= 3) {
                try {
                  const res = await api.get(`/islands/check-name?name=${val}`);
                  setIslandNameAvailable(res.data.available);
                } catch { setIslandNameAvailable(null); }
              } else {
                setIslandNameAvailable(null);
              }
            }}
            className="flex-1 h-10 rounded-lg text-sm"
            placeholder="Es: Aurora47"
            maxLength={30}
            data-testid="island-name-input"
          />
          <Button
            size="sm"
            disabled={!newIslandName || newIslandName === islandName || islandNameAvailable === false}
            onClick={async () => {
              try {
                const res = await api.post('/islands/set-name', { island_name: newIslandName });
                toast.success(res.data.message);
                setIslandName(newIslandName);
                setNewIslandName("");
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Errore');
              }
            }}
            className="h-10 rounded-lg px-4"
          >
            Salva
          </Button>
        </div>
        {newIslandName && newIslandName !== islandName && (
          <p className={`text-xs mt-1 ${islandNameAvailable ? 'text-green-500' : islandNameAvailable === false ? 'text-red-500' : 'text-gray-400'}`}>
            {islandNameAvailable ? 'Disponibile' : islandNameAvailable === false ? 'Non disponibile' : '...'}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between py-2">
        <div>
          <p className="text-sm text-gray-900">Ricerca isola attiva</p>
          <p className="text-xs text-gray-400">Gli altri possono trovarti cercando il nome isola</p>
        </div>
        <Switch
          checked={true}
          onCheckedChange={async (val) => {
            try {
              await api.put('/islands/privacy-settings', { island_search_enabled: val });
              toast.success('Impostazione aggiornata');
            } catch { toast.error('Errore'); }
          }}
        />
      </div>
      <div className="flex items-center justify-between py-2">
        <div>
          <p className="text-sm text-gray-900">Richieste di collegamento isola</p>
          <p className="text-xs text-gray-400">Chi puo inviarti richieste via isola</p>
        </div>
        <select
          defaultValue="everyone"
          onChange={async (e) => {
            try {
              await api.put('/islands/privacy-settings', { island_requests_from: e.target.value });
              toast.success('Impostazione aggiornata');
            } catch { toast.error('Errore'); }
          }}
          className="text-sm border rounded-lg px-2 py-1.5 text-gray-600"
        >
          <option value="everyone">Tutti</option>
          <option value="verified">Solo verificati</option>
          <option value="nobody">Nessuno</option>
        </select>
      </div>

      <Separator className="my-3" />

      <Button
        onClick={handleSave}
        disabled={saving}
        variant="outline"
        className="w-full h-10 rounded-lg border-gray-200 text-gray-700 font-normal mt-4"
      >
        {saving ? '...' : 'Salva'}
      </Button>
    </div>
  );
  
  const renderNotifications = () => (
    <div className="px-4 py-4 space-y-4">
      <PushNotificationSettings />
      
      <div className="mt-6">
        <h4 className="text-xs text-gray-400 uppercase tracking-wide mb-3">Preferenze Notifiche</h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-gray-900">Nuovi regali</p>
              <p className="text-xs text-gray-400">Quando ricevi un regalo</p>
            </div>
            <Switch defaultChecked={true} />
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-gray-900">Richieste di connessione</p>
              <p className="text-xs text-gray-400">Quando qualcuno vuole collegarsi</p>
            </div>
            <Switch defaultChecked={true} />
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-gray-900">Connessioni accettate</p>
              <p className="text-xs text-gray-400">Quando accettano la tua richiesta</p>
            </div>
            <Switch defaultChecked={true} />
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-gray-900">Likes e commenti</p>
              <p className="text-xs text-gray-400">Attività sui tuoi post</p>
            </div>
            <Switch defaultChecked={true} />
          </div>
        </div>
      </div>
    </div>
  );
  
  const renderWallet = () => (
    <div style={{ background: "linear-gradient(180deg, #0a0a0f 0%, #1a1020 50%, #0a0a0f 100%)", margin: -16, padding: 16, minHeight: "80vh" }}>
      {/* Hero coin card */}
      <div className="relative overflow-hidden rounded-2xl p-6 mb-5 text-center" style={{ background: "radial-gradient(ellipse at 50% 30%, #2a1a0a 0%, #0f0a05 70%)", border: "1px solid #D4A57433" }}>
        {/* Glow effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full" style={{ background: "radial-gradient(circle, #f9731622 0%, transparent 70%)", filter: "blur(30px)" }} />
        <div className="absolute bottom-0 right-0 w-32 h-32 rounded-full" style={{ background: "radial-gradient(circle, #ec489922 0%, transparent 70%)", filter: "blur(20px)" }} />
        
        {/* Coin image */}
        <div className="relative w-24 h-24 mx-auto mb-3">
          <div className="w-full h-full rounded-full flex items-center justify-center" style={{
            background: "linear-gradient(135deg, #D4A574, #8B6914)",
            boxShadow: "0 0 40px #D4A57444, 0 0 80px #f9731611, inset 0 2px 4px #ffffff22",
            border: "2px solid #D4A57466",
          }}>
            <img src="/images/ade-logo-extracted.png" alt="ADE" className="w-14 h-14 object-contain" style={{ filter: "brightness(1.3)" }} />
          </div>
          {/* Pulse ring */}
          <div className="absolute inset-0 rounded-full" style={{ border: "1px solid #D4A57444", animation: "coin-glow 3s ease-in-out infinite" }} />
        </div>
        
        <p className="text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: "#D4A574" }}>{t('yourBalance')}</p>
        <p className="text-4xl font-extrabold mb-1" style={{ background: "linear-gradient(135deg, #D4A574, #f97316, #ec4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          {wallet.balance.toLocaleString()}
        </p>
        <p className="text-xs mb-5" style={{ color: "#D4A57488" }}>Crediti ADE</p>
        
        <button
          onClick={() => setShowCoinShop(true)}
          className="inline-flex items-center gap-2 px-6 py-2.5 font-semibold rounded-xl text-sm transition-all hover:scale-105 active:scale-95"
          style={{ background: "linear-gradient(135deg, #f97316, #ec4899)", color: "#fff", boxShadow: "0 4px 20px #f9731644" }}
        >
          <ShoppingCart className="w-4 h-4" />
          {t('buyCoins')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-xl p-4" style={{ background: "#ffffff08", border: "1px solid #ffffff0a" }}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #f97316, #D4A574)" }}>
              <Gift className="w-4 h-4 text-white" />
            </div>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: "#D4A574" }}>{t('sent')}</p>
          </div>
          <p className="text-2xl font-bold text-white">{wallet.gifts_sent}</p>
        </div>
        <div className="rounded-xl p-4" style={{ background: "#ffffff08", border: "1px solid #ffffff0a" }}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #ec4899, #a855f7)" }}>
              <Gift className="w-4 h-4 text-white" />
            </div>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: "#ec4899" }}>{t('received')}</p>
          </div>
          <p className="text-2xl font-bold text-white">{wallet.gifts_received}</p>
        </div>
      </div>

      <CoinShop isOpen={showCoinShop} onClose={() => setShowCoinShop(false)} 
        onPurchaseComplete={() => { api.get('/wallet').then(res => setWallet(res.data)).catch(() => {}); }} />

      <style>{`
        @keyframes coin-glow {
          0%, 100% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.15); opacity: 0; }
        }
      `}</style>
    </div>
  );

  const renderGoldenRing = () => {
    const level = user?.golden?.level || 0;
    const giftsReceived = user?.golden?.gifts_count || wallet.gifts_received || 0;
    
    const levels = [
      { level: 1, name: "Bronze Ring", min: 10, color: "#CD7F32", gradient: "linear-gradient(135deg, #CD7F32, #A0522D)" },
      { level: 2, name: "Silver Ring", min: 50, color: "#C0C0C0", gradient: "linear-gradient(135deg, #C0C0C0, #808080)" },
      { level: 3, name: "Gold Ring", min: 200, color: "#FFD700", gradient: "linear-gradient(135deg, #FFD700, #B8860B)" },
      { level: 4, name: "Platinum Ring", min: 500, color: "#E5E4E2", gradient: "linear-gradient(135deg, #E5E4E2, #A9A9A9)" },
      { level: 5, name: "Diamond Ring", min: 1000, color: "#B9F2FF", gradient: "linear-gradient(135deg, #B9F2FF, #4FC3F7)" },
    ];

    const currentLevel = levels.find(l => l.level === level);
    const nextLevel = levels.find(l => l.level === level + 1) || levels[levels.length - 1];
    const progressToNext = level < 5 ? Math.min(100, (giftsReceived / nextLevel.min) * 100) : 100;
    const ringColor = level > 0 ? currentLevel.color : "#555";

    return (
      <div style={{ background: "linear-gradient(180deg, #0a0a0f 0%, #0f0a15 50%, #0a0a0f 100%)", margin: -16, padding: 16, minHeight: "80vh" }}>
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl p-6 mb-5 text-center" style={{ background: `radial-gradient(ellipse at 50% 40%, ${ringColor}11 0%, #0a0a0f 70%)`, border: `1px solid ${ringColor}22` }}>
          {/* Ambient glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full" style={{ background: `radial-gradient(circle, ${ringColor}15 0%, transparent 60%)`, filter: "blur(40px)" }} />
          
          {/* Ring visual */}
          <div className="relative w-28 h-28 mx-auto mb-4">
            <div className="w-full h-full rounded-full flex items-center justify-center" style={{
              background: `radial-gradient(circle at 40% 35%, ${ringColor}33 0%, #0a0a0f 70%)`,
              border: `3px solid ${ringColor}`,
              boxShadow: `0 0 40px ${ringColor}33, 0 0 80px ${ringColor}11, inset 0 0 30px ${ringColor}11`,
            }}>
              <Star className="w-12 h-12" style={{ color: ringColor, filter: `drop-shadow(0 0 8px ${ringColor}66)` }} />
            </div>
            <div className="absolute inset-0 rounded-full" style={{ border: `1px solid ${ringColor}33`, animation: "ring-glow 3s ease-in-out infinite" }} />
          </div>
          
          {level > 0 ? (
            <>
              <h2 className="text-2xl font-extrabold text-white mb-0.5">{currentLevel.name}</h2>
                <p className="text-xs" style={{ color: `${ringColor}88` }}>{t('level')} {level}</p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-extrabold text-white/70 mb-0.5">{t('noRing')}</h2>
              <p className="text-xs text-white/30">{t('receiveGiftsToLevel')}</p>
            </>
          )}
        </div>

        {/* Progress */}
        {level < 5 && (
          <div className="rounded-2xl p-4 mb-5" style={{ background: "#ffffff06", border: "1px solid #ffffff0a" }}>
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-semibold text-white/80">{t('nextLevel')}</p>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ background: nextLevel.gradient }} />
                <span className="text-xs font-medium" style={{ color: nextLevel.color }}>{nextLevel.name}</span>
              </div>
            </div>
            <div className="h-3 rounded-full overflow-hidden mb-2" style={{ background: "#ffffff0a" }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progressToNext}%`, background: nextLevel.gradient, boxShadow: `0 0 10px ${nextLevel.color}44` }} />
            </div>
            <p className="text-xs text-white/30">{giftsReceived} / {nextLevel.min} {t('giftsReceived')}</p>
          </div>
        )}

        {/* All levels */}
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: "#ffffff33" }}>{t('allLevels')}</p>
          {levels.map(l => {
            const isActive = l.level <= level;
            const isCurrent = l.level === level;
            return (
              <div key={l.level} className="flex items-center gap-3 p-3 rounded-xl transition-all" style={{
                background: isCurrent ? `${l.color}0a` : "#ffffff04",
                border: isCurrent ? `1px solid ${l.color}33` : "1px solid #ffffff06",
                boxShadow: isCurrent ? `0 0 20px ${l.color}11` : "none",
              }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{
                  background: isActive ? l.gradient : "#ffffff08",
                  boxShadow: isCurrent ? `0 0 16px ${l.color}33` : "none",
                }}>
                  <Star className="w-5 h-5" style={{ color: isActive ? "#fff" : "#ffffff22" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-white/25'}`}>{l.name}</p>
                  <p className="text-[10px]" style={{ color: "#ffffff33" }}>{l.min}+ regali ricevuti</p>
                </div>
                {isCurrent && (
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full text-white" style={{ background: l.gradient, boxShadow: `0 0 10px ${l.color}33` }}>ATTIVO</span>
                )}
                {!isActive && l.level === level + 1 && (
                  <span className="text-[10px] font-medium text-white/20">Prossimo</span>
                )}
              </div>
            );
          })}
        </div>

        <style>{`
          @keyframes ring-glow {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.12); opacity: 0; }
          }
        `}</style>
      </div>
    );
  };
  
  const renderSecurity = () => (
    <div className="px-4 py-4 space-y-2">
      {/* Privacy & Data Center (Section 10) */}
      <p className="text-xs text-gray-400 uppercase tracking-wide py-1 mb-2">{t('privacy')} & Data</p>
      
      <a href="/privacy" target="_blank" className="w-full flex items-center justify-between py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg px-2">
        Privacy Policy
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </a>
      <a href="/terms" target="_blank" className="w-full flex items-center justify-between py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg px-2">
        Terms of Service
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </a>
      
      <Separator className="my-2" />
      <p className="text-xs text-gray-400 uppercase tracking-wide py-1">Visibilità Account</p>
      
      <div className="flex items-center justify-between py-3 px-2">
        <div>
          <p className="text-sm text-gray-700">Visibile e cercabile</p>
          <p className="text-xs text-gray-400">Se disattivato, ti trovano solo tramite vicinanza o nome Isola</p>
        </div>
        <Switch checked={discoverable} onCheckedChange={async (val) => {
          setDiscoverable(val);
          try {
            await api.put('/profile/settings', { discoverable: val });
          } catch { toast.error(t('saveError')); }
        }} data-testid="discoverable-toggle" />
      </div>
      
      <div className="flex items-center justify-between py-3 px-2">
        <div>
          <p className="text-sm text-gray-700">{t("readReceiptsLabel")}</p>
          <p className="text-xs text-gray-400">{t("readReceiptsDesc")}</p>
        </div>
        <Switch checked={readReceipts} onCheckedChange={async (val) => {
          setReadReceipts(val);
          try {
            await api.put('/profile/settings', { read_receipts: val });
          } catch { toast.error(t('saveError')); }
        }} data-testid="read-receipts-toggle" />
      </div>
      
      <div className="flex items-center justify-between py-3 px-2">
        <div>
          <p className="text-sm text-gray-700">{t("hideIdentityLabel")}</p>
          <p className="text-xs text-gray-400">{t("hideIdentityDesc")}</p>
        </div>
        <Switch checked={hideIdentity} onCheckedChange={async (val) => {
          setHideIdentity(val);
          try {
            await api.put('/profile/settings', { hide_identity: val });
          } catch { toast.error(t('saveError')); }
        }} data-testid="hide-identity-toggle" />
      </div>
      
      <Separator className="my-2" />
      <p className="text-xs text-gray-400 uppercase tracking-wide py-1">Consensi</p>
      
      <div className="flex items-center justify-between py-3 text-sm px-2">
        <span className="text-gray-700">Marketing</span>
        <button
          onClick={async () => {
            try {
              await api.post('/privacy/revoke-marketing');
              toast.success('Consenso marketing revocato');
            } catch (e) { toast.error('Errore'); }
          }}
          className="text-xs text-red-500 font-medium"
          data-testid="revoke-marketing-btn"
        >
          Revoca
        </button>
      </div>
      <div className="flex items-center justify-between py-3 text-sm px-2">
        <span className="text-gray-700">Geolocalizzazione</span>
        <button
          onClick={async () => {
            try {
              await api.post('/privacy/revoke-location');
              toast.success('Consenso posizione revocato');
            } catch (e) { toast.error('Errore'); }
          }}
          className="text-xs text-red-500 font-medium"
          data-testid="revoke-location-btn"
        >
          Revoca
        </button>
      </div>
      
      <Separator className="my-2" />
      <p className="text-xs text-gray-400 uppercase tracking-wide py-1">I tuoi dati</p>
      
      <button 
        onClick={async () => {
          try {
            toast.info('Preparazione dati in corso...');
            const res = await api.get('/privacy/export-data');
            const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ade-data-export-${new Date().toISOString().split('T')[0]}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Dati esportati!');
          } catch (e) { toast.error('Errore nell\'esportazione'); }
        }}
        className="w-full flex items-center justify-between py-3 text-sm text-blue-600 hover:bg-blue-50 rounded-lg px-2"
        data-testid="export-data-btn"
      >
        <div className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          Scarica i tuoi dati (GDPR Art. 20)
        </div>
        <ChevronRight className="w-4 h-4" />
      </button>
      
      <Separator className="my-2" />
      <p className="text-xs text-gray-400 uppercase tracking-wide py-1">Account</p>
      
      <button 
        onClick={handleLogout}
        className="w-full flex items-center justify-between py-3 text-sm text-gray-500 hover:bg-gray-50 rounded-lg px-2"
      >
        Esci
        <LogOut className="w-4 h-4" />
      </button>
      <button 
        onClick={() => setShowDeleteDialog(true)}
        className="w-full flex items-center justify-between py-3 text-sm text-red-500 hover:bg-red-50 rounded-lg px-2"
        data-testid="delete-account-btn"
      >
        <div className="flex items-center gap-2">
          <Trash2 className="w-4 h-4" />
          Elimina account e tutti i dati
        </div>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
  
  const renderUsername = () => (
    <div className="px-4 py-4 space-y-4">
      <div>
        <label className="text-xs text-gray-400 uppercase tracking-wide">Il tuo username</label>
        <div className="flex gap-2 mt-2">
          <div className="relative flex-1">
            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              className="h-10 pl-9 border-gray-200 rounded-lg"
              maxLength={20}
              placeholder="username"
              data-testid="username-input"
            />
          </div>
          <Button
            onClick={handleUpdateUsername}
            disabled={saving || newUsername === username || usernameCheck?.available === false || checkingUsername}
            className="h-10 px-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg"
            data-testid="username-save-btn"
          >
            {saving ? '...' : 'Salva'}
          </Button>
        </div>
        {newUsername && newUsername !== username && (
          <p className={`text-xs mt-1.5 flex items-center gap-1 ${checkingUsername ? 'text-gray-400' : usernameCheck?.available ? 'text-green-600' : 'text-red-500'}`} data-testid="username-availability">
            {checkingUsername ? 'Verifica...' : usernameCheck ? (
              usernameCheck.available ? (
                <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" strokeWidth={3} /> @{newUsername} è disponibile</span>
              ) : (
                <span className="inline-flex items-center gap-1"><X className="w-3.5 h-3.5" strokeWidth={3} /> {usernameCheck.reason}</span>
              )
            ) : ''}
          </p>
        )}
        <p className="text-xs text-gray-400 mt-2">
          3-20 caratteri. Puoi cambiarlo solo una volta al mese.
        </p>
      </div>
      
      <Separator />
      
      <div className="flex items-center justify-between py-2">
        <div>
          <p className="text-sm text-gray-900">Mostra username</p>
          <p className="text-xs text-gray-400">Usa @{username || 'username'} invece del tuo nome</p>
        </div>
        <Switch 
          checked={displayUsername} 
          onCheckedChange={handleToggleDisplayName}
          disabled={!username}
        />
      </div>
      
      <div className="bg-gray-50 rounded-xl p-4 mt-4">
        <p className="text-xs text-gray-500 mb-1">Anteprima:</p>
        <p className="text-lg font-medium text-gray-900">
          {displayUsername && username ? `@${username}` : name || 'Il tuo nome'}
        </p>
      </div>
    </div>
  );
  
  const renderLanguage = () => (
    <div className="px-4 py-4">
      <p className="text-sm text-gray-500 mb-4">
        Seleziona la lingua dell'app. Alcune traduzioni potrebbero non essere complete.
      </p>
      <div className="space-y-1">
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${
              selectedLanguage === lang.code 
                ? 'bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200' 
                : 'hover:bg-gray-50'
            }`}
            data-testid={`lang-${lang.code}`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{lang.flag}</span>
              <span className={`text-sm ${selectedLanguage === lang.code ? 'text-orange-600 font-medium' : 'text-gray-700'}`}>
                {lang.name}
              </span>
            </div>
            {selectedLanguage === lang.code && (
              <Check className="w-5 h-5 text-orange-500" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
  
  // Main menu — premium design with all ADE categories
  const renderMenu = () => {
    const MenuItem = ({ icon: Icon, iconBg, label, value, onClick, badge }) => (
      <button onClick={onClick} className="w-full flex items-center gap-3 py-3 px-3 set-glass-row transition-all active:scale-[0.985]" data-testid={`menu-${label.toLowerCase().replace(/\s/g,'-')}`}>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-br ${iconBg}`} style={{ boxShadow: "0 4px 10px rgba(0,0,0,0.12)" }}>
          <Icon className="w-[18px] h-[18px] text-white" />
        </div>
        <span className="text-[15px] text-gray-900 flex-1 text-left">{label}</span>
        {badge && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white mr-1">{badge}</span>}
        {value && <span className="text-sm text-gray-400 mr-1">{value}</span>}
        <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
      </button>
    );

    const NeonRow = ({ icon: Icon, color, g1, g2, title, subtitle, onClick, badge, testId }) => (
      <button onClick={onClick} className="w-full flex items-center gap-3.5 p-4 rounded-2xl transition-all active:scale-[0.98] text-left"
        style={{
          background: isDark ? "rgba(18,24,44,0.55)" : "rgba(255,255,255,0.72)",
          border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(0,0,0,0.05)",
          boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.35)" : "0 2px 12px rgba(20,20,40,0.05)",
          backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
        }} data-testid={testId}>
        {isDark ? (
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ border: `1.5px solid ${color}70`, boxShadow: `0 0 14px ${color}45, inset 0 0 10px ${color}22` }}>
            <Icon className="w-5 h-5" strokeWidth={1.6} style={{ color, filter: `drop-shadow(0 0 4px ${color})` }} />
          </div>
        ) : (
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 5px 12px ${g2}50` }}>
            <Icon className="w-5 h-5 text-white" strokeWidth={1.8} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{title}</p>
          <p className={`text-xs ${isDark ? "text-white/40" : "text-gray-400"}`}>{subtitle}</p>
        </div>
        {badge}
        <ChevronRight className="w-4 h-4 shrink-0" style={{ color: isDark ? color : g2 }} />
      </button>
    );

    const SectionLabel = ({ children }) => (
      <p className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold px-1 pt-5 pb-1">{children}</p>
    );

    return (
      <div className="px-4 pb-8">
        {/* Profile card */}
        <button onClick={() => setActiveSection('account')} className="w-full flex items-center gap-3 p-3 my-3 set-glass-row transition-all active:scale-[0.985]">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center overflow-hidden ring-2 ring-white">
            {user?.picture && !user.picture.includes('default') ? (
              <img src={user.picture.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${user.picture}` : user.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <User className="w-6 h-6 text-white" />
            )}
          </div>
          <div className="flex-1 text-left">
            <p className="text-[15px] font-semibold text-gray-900">{name || 'Il tuo profilo'}</p>
            <p className="text-xs text-gray-400">{username ? `@${username}` : user?.email}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300" />
        </button>

        <SectionLabel>{t('account')}</SectionLabel>
        <MenuItem icon={User} iconBg="from-blue-400 to-blue-600" label={t('profile')} onClick={() => setActiveSection('account')} />
        <MenuItem icon={AtSign} iconBg="from-indigo-400 to-indigo-600" label={t('username')} value={username ? `@${username}` : t('notSet')} onClick={() => setActiveSection('username')} />

        <SectionLabel>ADE ISLAND</SectionLabel>
        <MenuItem icon={Map} iconBg="from-cyan-400 to-cyan-600" label="La mia isola" value={user?.island_name || ''} onClick={() => navigate('/app/here')} />
        <MenuItem icon={ShoppingCart} iconBg="from-teal-400 to-teal-600" label="Island Shop" onClick={() => navigate('/app/here?shop=1')} />

        <SectionLabel>ECONOMIA</SectionLabel>
        {/* Premium Economy Menu with descriptions and status */}
        <div className="space-y-1.5">
          <button onClick={() => navigate('/app/credits')} className="w-full flex items-center gap-3.5 p-4 set-glass-row transition-all active:scale-[0.98] group" data-testid="menu-credits">
            <div className="w-11 h-11 rounded-xl overflow-hidden flex items-center justify-center ade-coin-box">
              <img src="/images/ade-coin.jpeg" alt="ADE Coin" className="w-9 h-9 rounded-full object-cover" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-gray-900">{t('adeCoins')}</p>
              <p className="text-xs text-gray-400">{Math.floor(wallet.balance).toLocaleString()} crediti disponibili</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
          </button>

          <MenuItem icon={Building2} iconBg="from-blue-400 to-blue-600" label="Dati di fatturazione" value="P.IVA e fattura" onClick={() => setShowBilling(true)} />
          <BillingDataDialog open={showBilling} onClose={() => setShowBilling(false)} />

          <NeonRow
            icon={Gift}
            color="#FF3B5C"
            g1="#FF6FA5" g2="#E91E63"
            title="Gift"
            subtitle="I tuoi regali inviati e ricevuti"
            onClick={() => navigate('/app/gift-center')}
            testId="menu-gifts"
          />

          <NeonRow
            icon={Crown}
            color="#D4AF37"
            g1="#F7CE55" g2="#E8A317"
            title={t('goldenRing')}
            subtitle={user?.golden?.tier ? `${user.golden.tier.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} Ring — Verificato` : 'Verifica il tuo status finanziario'}
            onClick={() => navigate('/app/golden-ring')}
            badge={user?.golden?.tier ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#D4AF3722', color: '#D4AF37', border: '1px solid #D4AF3755' }}>
                {user.golden.tier.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            ) : null}
            testId="menu-golden-ring"
          />

          <NeonRow
            icon={Award}
            color="#10B981"
            g1="#2DD4BF" g2="#0D9488"
            title="Creator Program"
            subtitle={user?.creator_status === 'approved' ? 'Programma attivo' : 'Diventa Creator'}
            onClick={() => navigate(user?.creator_status === 'approved' ? '/app/creator-dashboard' : '/app/creator-apply')}
            badge={user?.creator_status === 'approved' ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#10B98122', color: '#10B981', border: '1px solid #10B98155' }}>Attivo</span>
            ) : null}
            testId="menu-creator"
          />
          <NeonRow
            icon={Building2}
            color="#3B82F6"
            g1="#3B82F6" g2="#1D4ED8"
            title="ADE Pro"
            subtitle={user?.profile_type === 'professional' ? 'Attivo — modifica' : 'Passa ad ADE Pro'}
            onClick={() => navigate('/app/settings/professional')}
            badge={user?.profile_type === 'professional' ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#3B82F622', color: '#3B82F6', border: '1px solid #3B82F655' }}>PRO</span>
            ) : null}
            testId="menu-professional"
          />
        </div>

        <SectionLabel>{t('privacy')} & DATA</SectionLabel>
        <MenuItem icon={Eye} iconBg="from-slate-400 to-slate-600" label={t('visibility')} onClick={() => setActiveSection('privacy')} />
        <MenuItem icon={Lock} iconBg="from-rose-400 to-rose-600" label={t('changePasswordTitle')} onClick={() => navigate('/app/change-password')} />
        <MenuItem icon={Shield} iconBg="from-emerald-400 to-emerald-600" label="Privacy & Dati" onClick={() => setActiveSection('security')} />
        <MenuItem icon={FileText} iconBg="from-gray-400 to-gray-600" label="Privacy Policy" onClick={() => window.open('/privacy', '_blank')} />
        <MenuItem icon={FileText} iconBg="from-gray-400 to-gray-600" label="Termini di Servizio" onClick={() => window.open('/terms', '_blank')} />

        <SectionLabel>{t('notificationsMenu')}</SectionLabel>
        <MenuItem icon={Bell} iconBg="from-orange-400 to-orange-600" label={t('pushPreferences')} onClick={() => setActiveSection('notifications')} />

        <SectionLabel>{t('preferences')}</SectionLabel>
        <MenuItem icon={Globe} iconBg="from-violet-400 to-violet-600" label={t('language')} value={`${LANGUAGES.find(l => l.code === selectedLanguage)?.flag || ''} ${LANGUAGES.find(l => l.code === selectedLanguage)?.name || ''}`} onClick={() => setActiveSection('language')} />
        <div className="flex items-center gap-3.5 p-4 set-glass-row transition-all">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center bg-gradient-to-br ${isDark ? 'from-indigo-400 to-indigo-700' : 'from-amber-400 to-amber-600'}`} style={{ boxShadow: "0 4px 10px rgba(0,0,0,0.12)" }}>
            {isDark ? <Moon className="w-5 h-5 text-white" /> : <Sun className="w-5 h-5 text-white" />}
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-gray-900">Aspetto</p>
            <p className="text-xs text-gray-400">{isDark ? 'Modalità scura' : 'Modalità chiara'}</p>
          </div>
          <Switch checked={isDark} onCheckedChange={() => {
            const next = !isDark;
            toggleTheme();
            api.put('/profile/settings', { theme_preference: next ? 'dark' : 'light' }).catch(() => {});
          }} data-testid="dark-mode-toggle" />
        </div>

        <SectionLabel>INFO</SectionLabel>
        <MenuItem icon={Info} iconBg="from-gray-300 to-gray-500" label="Informazioni" value="v1.0" onClick={() => {}} />

        {/* Logout */}
        <div className="mt-6">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full h-12 rounded-2xl border-red-200 text-red-500 hover:bg-red-50 font-medium"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('logout')}
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-[10px] text-gray-400">A.d.E - Around. Discover. Engage — Bolzano, Italia</p>
          <p className="text-[10px] text-gray-400">P.IVA 03062850999</p>
        </div>
      </div>
    );
  };
  
  const getSectionTitle = () => {
    switch(activeSection) {
      case 'account': return t('profile');
      case 'username': return t('username');
      case 'privacy': return t('visibility');
      case 'notifications': return t('notifications');
      case 'wallet': return t('adeCoins');
      case 'goldenring': return t('goldenRing');
      case 'security': return t('security');
      case 'language': return t('language');
      default: return t('settings');
    }
  };
  
  return (
    <div className="min-h-screen set-glass-page" data-testid="settings-page">
      {/* Header */}
      <div className="sticky top-0 z-20 set-glass-header flex items-center gap-3 px-4 py-3 safe-top">
        <button
          onClick={() => activeSection ? setActiveSection(null) : navigate(-1)}
          className="p-1.5 -ml-1.5 rounded-full hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
        </button>
        <h1 className="text-base font-medium text-gray-900">{getSectionTitle()}</h1>
      </div>
      
      {/* Content */}
      {!activeSection && renderMenu()}
      {activeSection === 'account' && renderAccount()}
      {activeSection === 'username' && renderUsername()}
      {activeSection === 'privacy' && renderPrivacy()}
      {activeSection === 'notifications' && renderNotifications()}
      {activeSection === 'wallet' && renderWallet()}
      {activeSection === 'goldenring' && renderGoldenRing()}
      {activeSection === 'security' && renderSecurity()}
      {activeSection === 'language' && renderLanguage()}
      
      {/* Delete Account Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Elimina Account</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il tuo account? Questa azione è <strong>irreversibile</strong>. 
              Tutti i tuoi dati, foto, connessioni e crediti ADE verranno eliminati permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Suspend Account Dialog */}
      <AlertDialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-600">Sospendi Account</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi sospendere temporaneamente il tuo account? 
              Il tuo profilo non sarà visibile agli altri utenti. 
              Potrai riattivarlo in qualsiasi momento effettuando il login.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSuspendAccount}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              Sospendi account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
